import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { normalizeCheckoutPhone } from "@/lib/normalizeCheckoutPhone";
import PhoneInput from "react-phone-number-input";
import type { E164Number } from "libphonenumber-js";
import { Button } from "@/components/ui/button";
import {
  fetchLeadSession,
  requestLeadSessionCode,
} from "./leadSessionClient";
import {
  meetingRequiresAuthentication,
  productHasPrice,
} from "@/lib/productHasPrice";
import {
  MeetingAuthenticationTypeEnum,
  isMeetingAuthenticationChannelAllowed,
  resolveAllowedAuthenticationTypes,
} from "@/types/MeetingAuthenticationType";
import { HavingTroubleLink } from "./meeting/HavingTroubleLink";
import { LivestreamStartDateDisplay } from "./meeting/LivestreamStartDateDisplay";
import { WebsiteThemeScope } from "./WebsiteThemeScope";
import {
  parseWebsiteDesignSettingsTheme,
  type WebsiteDesignSettingsTheme,
} from "@/utils/websiteDesignTheme";

type DeliveryChannel = "phone" | "email";

function contactPrefillFromSearchParams(searchParams: URLSearchParams) {
  const email = searchParams.get("email")?.trim() ?? "";
  const rawPhone = searchParams.get("phone")?.trim() ?? "";
  const parsed = normalizeCheckoutPhone(rawPhone);
  const phone = parsed.ok ? (parsed.e164 ?? "") : rawPhone;
  return { email, phone };
}

interface MeetingProduct {
  _id: string;
  name?: string;
  images?: { url?: string }[];
  price?: number;
  variants?: { price?: number }[];
  meeting?: {
    requireAuthentication?: boolean;
    allowedAuthenticationTypes?: string[];
    start?: string | null;
    end?: string | null;
  };
}

function resolveInitialChannel({
  prefill,
  allowedTypes,
}: {
  prefill: { email: string; phone: string };
  allowedTypes: MeetingAuthenticationTypeEnum[];
}): DeliveryChannel {
  const allowsEmail = allowedTypes.includes(MeetingAuthenticationTypeEnum.EMAIL);
  const allowsSms = allowedTypes.includes(MeetingAuthenticationTypeEnum.SMS);

  if (prefill.phone && allowsSms) return "phone";
  if (prefill.email && allowsEmail) return "email";
  if (allowsSms) return "phone";
  return "email";
}

function authenticationPrompt({
  hasPrice,
  allowedTypes,
}: {
  hasPrice: boolean;
  allowedTypes: MeetingAuthenticationTypeEnum[];
}): string {
  const allowsEmail = allowedTypes.includes(MeetingAuthenticationTypeEnum.EMAIL);
  const allowsSms = allowedTypes.includes(MeetingAuthenticationTypeEnum.SMS);

  if (allowsEmail && allowsSms) {
    return hasPrice
      ? "Enter the phone number or email you used to purchase to access this meeting"
      : "Enter a phone number or email to access this meeting";
  }
  if (allowsEmail) {
    return hasPrice
      ? "Enter the email you used to purchase to access this meeting"
      : "Enter your email to access this meeting";
  }
  return hasPrice
    ? "Enter the phone number you used to purchase to access this meeting"
    : "Enter your phone number to access this meeting";
}

export function MeetingAuthLoginRoute() {
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefill = contactPrefillFromSearchParams(searchParams);
  const [allowedAuthenticationTypes, setAllowedAuthenticationTypes] = useState(
    () => [...resolveAllowedAuthenticationTypes(undefined)],
  );
  const [channel, setChannel] = useState<DeliveryChannel>(() =>
    resolveInitialChannel({
      prefill,
      allowedTypes: resolveAllowedAuthenticationTypes(undefined),
    }),
  );
  const [phone, setPhone] = useState<E164Number | undefined>(
    prefill.phone ? (prefill.phone as E164Number) : undefined,
  );
  const [email, setEmail] = useState(prefill.email);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState<MeetingProduct | null>(null);
  const [artistName, setArtistName] = useState("");
  const [supportEmail, setSupportEmail] = useState<string | null>(null);
  const [theme, setTheme] = useState<WebsiteDesignSettingsTheme | null>(null);

  useEffect(() => {
    if (!productId) return;

    const load = async () => {
      try {
        const [productRes, authRes] = await Promise.all([
          fetch(`/api/products/${productId}`),
          fetchLeadSession({ productId }),
        ]);

        if (authRes.ok) {
          const authData = (await authRes.json()) as { authenticated?: boolean };
          if (authData.authenticated) {
            navigate(`/meetings/${productId}`, { replace: true });
            return;
          }
        }

        if (!productRes.ok) {
          setError("Meeting not found");
          return;
        }

        const data = (await productRes.json()) as {
          product?: MeetingProduct;
          artistName?: string;
          supportEmail?: string;
          websiteDesignSettings?: unknown;
        };

        if (!data.product || !meetingRequiresAuthentication(data.product)) {
          navigate(`/meetings/${productId}`, { replace: true });
          return;
        }

        const resolvedAllowedTypes = resolveAllowedAuthenticationTypes(
          data.product.meeting?.allowedAuthenticationTypes,
        );

        setProduct(data.product);
        setAllowedAuthenticationTypes(resolvedAllowedTypes);
        setChannel(
          resolveInitialChannel({
            prefill,
            allowedTypes: resolvedAllowedTypes,
          }),
        );
        setArtistName(data.artistName?.trim() ?? "");
        setSupportEmail(data.supportEmail?.trim() || null);
        setTheme(parseWebsiteDesignSettingsTheme(data.websiteDesignSettings));
      } catch {
        setError("Failed to load meeting");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [productId, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!productId) return;

    setError(null);
    if (
      !isMeetingAuthenticationChannelAllowed({
        channel,
        allowedTypes: allowedAuthenticationTypes,
      })
    ) {
      setError("This sign-in method is not allowed for this meeting");
      return;
    }

    if (channel === "phone") {
      if (!phone?.trim()) {
        setError("Phone number is required");
        return;
      }
    } else if (!email.trim()) {
      setError("Email address is required");
      return;
    }

    setSubmitting(true);
    try {
      const res = await requestLeadSessionCode({
        productId,
        channel,
        email: channel === "email" ? email.trim() : undefined,
        phoneNumber: channel === "phone" ? phone : undefined,
      });
      const data = (await res.json()) as { error?: string; sessionId?: string };
      if (!res.ok) {
        setError(data.error ?? "Something went wrong");
        return;
      }
      if (!data.sessionId) {
        setError("Missing session");
        return;
      }
      navigate(`/meetings/${productId}/verify`, {
        state: { sessionId: data.sessionId, delivery: channel },
        replace: true,
      });
    } catch {
      setError("Network error");
    } finally {
      setSubmitting(false);
    }
  }

  const productName = product?.name?.trim() || "Meeting";
  const imageUrl = product?.images?.[0]?.url;
  const meetingStart = product?.meeting?.start ?? null;
  const meetingEnd = product?.meeting?.end ?? null;
  const pageTitle = `${productName} · Sign in`;
  const allowsEmail = allowedAuthenticationTypes.includes(
    MeetingAuthenticationTypeEnum.EMAIL,
  );
  const allowsSms = allowedAuthenticationTypes.includes(
    MeetingAuthenticationTypeEnum.SMS,
  );
  const showChannelPicker = allowsEmail && allowsSms;

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-black text-white">
          Loading...
        </div>
      </>
    );
  }

  if (error && !product) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-black text-white px-4 text-center">
          {error}
        </div>
      </>
    );
  }

  return (
    <WebsiteThemeScope settings={theme}>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <div className="min-h-screen text-white flex flex-col bg-black">
        {artistName ? (
          <div className="flex shrink-0 justify-center px-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-6">
            <p className="text-lg font-semibold">{artistName}</p>
          </div>
        ) : null}
        <div className="flex flex-1 flex-col items-center px-6">
          <div className="w-full max-w-sm space-y-6">
            <div className="text-center">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={productName}
                  className="w-40 mx-auto mb-4 rounded-lg shadow-xl object-cover"
                />
              ) : null}
              <h1 className="text-2xl font-bold">{productName}</h1>
              <p className="text-gray-400 text-sm mt-2">
                {product
                  ? authenticationPrompt({
                      hasPrice: productHasPrice(product),
                      allowedTypes: allowedAuthenticationTypes,
                    })
                  : "Sign in to access this meeting"}
              </p>
              <LivestreamStartDateDisplay
                start={meetingStart}
                end={meetingEnd}
                eventTitle={productName}
                meetingUrl={productId ? `/meetings/${productId}` : undefined}
              />
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              {showChannelPicker ? (
                <div>
                  <span className="block text-sm text-white mb-2">Sign in with</span>
                  <div
                    className="flex rounded-lg border border-white/20 p-0.5 bg-white/5"
                    role="group"
                    aria-label="Phone or email"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setChannel("phone");
                        setError(null);
                      }}
                      className={
                        "flex-1 rounded-md py-2 text-sm font-medium transition-colors text-white " +
                        (channel === "phone"
                          ? "bg-white/15 shadow-sm"
                          : "opacity-90 hover:opacity-100")
                      }
                    >
                      Phone
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setChannel("email");
                        setError(null);
                      }}
                      className={
                        "flex-1 rounded-md py-2 text-sm font-medium transition-colors text-white " +
                        (channel === "email"
                          ? "bg-white/15 shadow-sm"
                          : "opacity-90 hover:opacity-100")
                      }
                    >
                      Email
                    </button>
                  </div>
                </div>
              ) : null}
              {channel === "phone" && allowsSms ? (
                <div>
                  <label htmlFor="phone" className="block text-sm text-white mb-1">
                    Phone number
                  </label>
                  <PhoneInput
                    defaultCountry="US"
                    countrySelectProps={{ unicodeFlags: true }}
                    value={phone}
                    onChange={setPhone}
                    limitMaxLength
                    className="PhoneInput w-full rounded-lg bg-white/10 border border-white/20 px-2 py-1.5 text-white [--PhoneInput-color--focus:var(--primary)] [--PhoneInputCountryFlag-borderColor:rgba(255,255,255,0.35)] focus-within:ring-2 focus-within:ring-primary [&_.PhoneInputInput]:min-h-9 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:text-white [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:border-0 [&_.PhoneInputInput]:placeholder:text-gray-500"
                    numberInputProps={{
                      id: "phone",
                      autoComplete: "tel",
                    }}
                  />
                </div>
              ) : allowsEmail ? (
                <div>
                  <label htmlFor="email" className="block text-sm text-white mb-1">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg bg-white/10 border border-white/20 px-3 py-2.5 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              ) : null}
              {error ? <p className="text-sm text-red-400">{error}</p> : null}
              <Button type="submit" disabled={submitting} className="mt-1.5 w-full">
                {submitting ? "Sending…" : "Get Access"}
              </Button>
              {product && productHasPrice(product) ? (
                <p className="text-center text-sm text-gray-400">
                  Don't have access?{" "}
                  <Link
                    to={`/meetings/${productId}`}
                    className="text-white underline underline-offset-2 hover:opacity-90"
                  >
                    Purchase access
                  </Link>
                </p>
              ) : null}
            </form>
          </div>
        </div>
        <div className="shrink-0 px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          <HavingTroubleLink supportEmail={supportEmail} />
        </div>
      </div>
    </WebsiteThemeScope>
  );
}
